

import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import html2pdf from 'html2pdf.js';
import CalculatorForm from './CalculatorForm';
import { motion } from 'framer-motion';

const SWPCalculator = () => {
  const [investment, setInvestment] = useState(1000000);
  const [monthlyWithdrawal, setMonthlyWithdrawal] = useState(10000);
  const [expectedReturn, setExpectedReturn] = useState(8);
  const [showForm, setShowForm] = useState(false);
  const [result, setResult] = useState(null);
  const [chartData, setChartData] = useState([]);

  const calculate = () => {
    let balance = investment;
    let months = 0;
    let totalWithdrawn = 0;
    const monthlyRate = expectedReturn / 12 / 100;
    const data = [];
    
    // Calculate how long the corpus will last
    while (balance > 0 && months < 600) {
      const interestEarned = balance * monthlyRate;
      balance = balance + interestEarned - monthlyWithdrawal;
      totalWithdrawn += monthlyWithdrawal;
      months++;
      
      if (balance < 0) {
        totalWithdrawn += balance;
        balance = 0;
        break;
      }
      
      // Record data every 12 months for chart
      if (months % 12 === 0) {
        data.push({
          year: months / 12,
          balance: Math.max(0, balance),
          withdrawn: totalWithdrawn
        });
      }
    }
    
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    const totalInterest = totalWithdrawn - investment;
    
    setResult({ 
      years, 
      remainingMonths, 
      totalWithdrawn, 
      totalInterest,
      finalBalance: balance,
      months
    });
    setChartData(data);
  };

  const handleFormSubmit = (userData) => {
    const element = document.getElementById('swp-result-content');
    const opt = {
      margin: [0.5, 0.5, 0.5, 0.5],
      filename: `SWP_Report_${userData.name}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
    };
    html2pdf().set(opt).from(element).save();
    setShowForm(false);
    
    const whatsappMessage = `Dear ${userData.name},\n\n📊 SWP (Systematic Withdrawal Plan) Summary:\n\n• Initial Investment: ₹${investment.toLocaleString()}\n• Monthly Withdrawal: ₹${monthlyWithdrawal.toLocaleString()}\n• Expected Return: ${expectedReturn}% p.a.\n• Your corpus will last for: ${result.years} years ${result.remainingMonths} months\n• Total Withdrawn: ₹${result.totalWithdrawn.toLocaleString()}\n• Total Interest Earned: ₹${result.totalInterest.toLocaleString()}\n\nFor personalized investment advice, contact us:\n📞 9726344477\n📧 Wisdom2secure@gmail.com\n\n- Wisdom Consultancy`;
    
    window.open(`https://wa.me/${userData.phone}?text=${encodeURIComponent(whatsappMessage)}`, '_blank');
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-lg p-6"
    >
      <h3 className="text-2xl font-bold text-primary mb-2">SWP Calculator</h3>
      <p className="text-gray-600 mb-6">Systematic Withdrawal Plan - Get regular income from your investments</p>
      
      <div className="grid md:grid-cols-2 gap-8">
        {/* Input Section */}
        <div className="space-y-5">
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Total Investment (₹)</label>
            <input
              type="number"
              className="input-field"
              value={investment}
              onChange={(e) => setInvestment(Number(e.target.value))}
            />
            <p className="text-xs text-gray-500 mt-1">Enter the lump sum amount you want to invest</p>
          </div>

          <div>
            <label className="block text-gray-700 font-semibold mb-2">Monthly Withdrawal (₹)</label>
            <input
              type="number"
              className="input-field"
              value={monthlyWithdrawal}
              onChange={(e) => setMonthlyWithdrawal(Number(e.target.value))}
            />
            <p className="text-xs text-gray-500 mt-1">Amount you want to withdraw every month</p>
          </div>

          <div>
            <label className="block text-gray-700 font-semibold mb-2">Expected Return (% p.a.)</label>
            <input
              type="range"
              min="1"
              max="15"
              step="0.5"
              value={expectedReturn}
              onChange={(e) => setExpectedReturn(Number(e.target.value))}
              className="w-full"
            />
            <div className="text-center font-semibold text-primary mt-1">{expectedReturn}%</div>
          </div>

          <button
            onClick={calculate}
            className="w-full btn-primary py-3 text-lg"
          >
            Calculate SWP
          </button>
        </div>

        {/* Results Section */}
        {result && (
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5">
            <h4 className="font-bold text-lg text-primary mb-4">SWP Summary</h4>
            <div className="space-y-3">
              <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                <span className="text-gray-600">Initial Investment:</span>
                <span className="font-semibold">₹{investment.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                <span className="text-gray-600">Monthly Withdrawal:</span>
                <span className="font-semibold">₹{monthlyWithdrawal.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                <span className="text-gray-600">Duration:</span>
                <span className="font-semibold text-primary">{result.years} years {result.remainingMonths} months</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                <span className="text-gray-600">Total Withdrawn:</span>
                <span className="font-semibold text-green-600">₹{result.totalWithdrawn.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center pt-2">
                <span className="text-gray-700 font-bold">Total Interest Earned:</span>
                <span className="font-bold text-primary text-xl">₹{result.totalInterest.toLocaleString()}</span>
              </div>
            </div>
            
            {result.finalBalance > 0 && (
              <div className="mt-4 p-3 bg-green-100 rounded-lg">
                <p className="text-sm text-green-800">
                  💡 After {result.years} years {result.remainingMonths} months, you'll still have 
                  <strong> ₹{result.finalBalance.toLocaleString()}</strong> remaining!
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Chart Section */}
      {chartData.length > 0 && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-8"
        >
          <h4 className="font-bold text-lg text-primary mb-4">Corpus Depletion Chart</h4>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="year" label={{ value: 'Years', position: 'bottom' }} />
                <YAxis label={{ value: 'Amount (₹)', angle: -90, position: 'left' }} />
                <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} />
                <Legend />
                <Line type="monotone" dataKey="balance" stroke="#82ca9d" name="Remaining Corpus" strokeWidth={2} />
                <Line type="monotone" dataKey="withdrawn" stroke="#8884d8" name="Total Withdrawn" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      )}

      {/* Hidden PDF Content */}
      {result && (
        <div id="swp-result-content" style={{ display: 'none' }}>
          <div style={{ padding: '20px', fontFamily: 'Arial' }}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <h1 style={{ color: '#1e3c72' }}>Wisdom Consultancy</h1>
              <p>E-44, Vasant Vihar Society, GIDC -Vadsar Road, Vadodara-390010</p>
              <p>Phone: 9726344477 | Email: Wisdom2secure@gmail.com</p>
              <hr />
              <h2>Systematic Withdrawal Plan (SWP) Report</h2>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px' }}>
              <tbody>
                <tr><td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}><strong>Initial Investment:</strong></td><td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>₹{investment.toLocaleString()}</td></tr>
                <tr><td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}><strong>Monthly Withdrawal:</strong></td><td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>₹{monthlyWithdrawal.toLocaleString()}</td></tr>
                <tr><td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}><strong>Expected Return:</strong></td><td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{expectedReturn}% p.a.</td></tr>
                <tr><td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}><strong>Duration:</strong></td><td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{result.years} years {result.remainingMonths} months</td></tr>
                <tr><td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}><strong>Total Withdrawn:</strong></td><td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>₹{result.totalWithdrawn.toLocaleString()}</td></tr>
                <tr><td style={{ padding: '8px' }}><strong>Total Interest Earned:</strong></td><td style={{ padding: '8px' }}>₹{result.totalInterest.toLocaleString()}</td></tr>
              </tbody>
            </table>
            <div style={{ marginTop: '20px', textAlign: 'center', fontSize: '12px', color: '#666' }}>
              <p>This is a system generated report. For personalized advice, consult our experts.</p>
            </div>
          </div>
        </div>
      )}

      {result && (
        <button
          onClick={() => setShowForm(true)}
          className="w-full mt-6 bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-all duration-300"
        >
          📄 Download Report & Share on WhatsApp
        </button>
      )}

      {showForm && <CalculatorForm onSubmit={handleFormSubmit} onClose={() => setShowForm(false)} calculatorType="SWP" />}
    </motion.div>
  );
};

export default SWPCalculator;